import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import {
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

import PatternBlockSelector from './PatternBlockSelector';
import DNALayerManager from './DNALayerManager';
import ModelSizingInput from './ModelSizingInput';
import PatternPreview from './PatternPreview';
import PatternExport from './PatternExport';

const steps = [
  'Select Pattern Block',
  'Configure DNA Layers',
  'Input Model Sizing',
  'Preview Pattern',
  'Export for CLO3D'
];

const PatternBuilder = ({ brandId, brand, userId }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [patternData, setPatternData] = useState({
    patternBlock: null,
    segmentDNA: null,
    brandDNA: null,
    collectionDNA: null,
    pieceDNA: null,
    modelSizing: {},
    mergedYAML: null,
    generatedSVG: null,
    generatedPieces: [],
  });

  const handleNext = async () => {
    if (activeStep === 0 && !patternData.patternBlock) {
      toast.error('Please select a pattern block');
      return;
    }
    if (activeStep === 2 && Object.keys(patternData.modelSizing).length === 0) {
      toast.error('Please input model sizing measurements');
      return;
    }
    if (activeStep === 2) {
      const ok = await handleGeneratePattern();
      if (!ok) return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleGeneratePattern = async () => {
    try {
      toast.loading('Generating pattern...', { id: 'generate' });
      const token = localStorage.getItem('token');
      const response = await fetch('/api/spif/generate-pattern', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          patternBlock: patternData.patternBlock,
          segmentDNA: patternData.segmentDNA,
          brandDNA: patternData.brandDNA,
          collectionDNA: patternData.collectionDNA,
          pieceDNA: patternData.pieceDNA,
          modelSizing: patternData.modelSizing,
        }),
      });
      if (!response.ok) throw new Error(`Generation failed: ${response.status}`);
      const result = await response.json();
      setPatternData(prev => ({
        ...prev,
        mergedYAML: result.mergedYAML || '',
        generatedSVG: result.svg || '',
        generatedPieces: result.pieces || [],
      }));
      const pieceCount = (result.pieces || []).length;
      toast.success(
        pieceCount > 1 ? `Pattern generated — ${pieceCount} pieces` : 'Pattern generated!',
        { id: 'generate' }
      );
      return true;
    } catch (error) {
      console.error('Error generating pattern:', error);
      toast.error(`Failed to generate pattern: ${error.message}`, { id: 'generate' });
      return false;
    }
  };

  const updatePatternData = (field, value) =>
    setPatternData(prev => ({ ...prev, [field]: value }));

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <PatternBlockSelector
            selectedBlock={patternData.patternBlock}
            onSelectBlock={(block) => updatePatternData('patternBlock', block)}
          />
        );
      case 1:
        return (
          <DNALayerManager
            brandId={brandId}
            brand={brand}
            userId={userId}
            segmentDNA={patternData.segmentDNA}
            brandDNA={patternData.brandDNA}
            collectionDNA={patternData.collectionDNA}
            pieceDNA={patternData.pieceDNA}
            onUpdateSegmentDNA={(dna) => updatePatternData('segmentDNA', dna)}
            onUpdateBrandDNA={(dna) => updatePatternData('brandDNA', dna)}
            onUpdateCollectionDNA={(dna) => updatePatternData('collectionDNA', dna)}
            onUpdatePieceDNA={(dna) => updatePatternData('pieceDNA', dna)}
          />
        );
      case 2:
        return (
          <ModelSizingInput
            patternBlock={patternData.patternBlock}
            modelSizing={patternData.modelSizing}
            onUpdateSizing={(sizing) => updatePatternData('modelSizing', sizing)}
          />
        );
      case 3:
        return (
          <PatternPreview
            patternBlock={patternData.patternBlock}
            mergedYAML={patternData.mergedYAML}
            generatedSVG={patternData.generatedSVG}
            generatedPieces={patternData.generatedPieces}
            onRegenerate={handleGeneratePattern}
          />
        );
      case 4:
        return (
          <PatternExport
            patternBlock={patternData.patternBlock}
            mergedYAML={patternData.mergedYAML}
            generatedSVG={patternData.generatedSVG}
            generatedPieces={patternData.generatedPieces}
            brandName={brand?.name}
          />
        );
      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>SPIF Pattern Builder</strong> — select a garment, configure DNA layers (optional), input measurements, then generate production-ready pattern pieces.
        </Typography>
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ minHeight: 400 }}>
          {renderStepContent(activeStep)}
        </CardContent>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button disabled={activeStep === 0} onClick={handleBack} startIcon={<BackIcon />}>
            Back
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep < steps.length - 1 && (
              <Button variant="contained" onClick={handleNext} endIcon={<NextIcon />}>
                {activeStep === 2 ? 'Generate Pattern' : 'Next'}
              </Button>
            )}
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default PatternBuilder;
